import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import { useStrictLocalization } from "@faire/web--source/common/localization";

import { SelfServeNodes } from "../../../types";
import { addOrEditItems } from "../../instantAnswers/orders-addOrEditItems";
import { cancelOrder } from "../../instantAnswers/orders-cancelOrder";
import { cannotFindAnOrder } from "../../instantAnswers/orders-cannotFindAnOrder";
import { damageMissing } from "../../instantAnswers/orders-damagedMissing";
import { editOrderInformation } from "../../instantAnswers/orders-editOrderInformation";
import { internationalOrderSettings } from "../../instantAnswers/orders-internationalOrderSettings";
import { RequestCommissionChanges } from "../../instantAnswers/orders-requestCommissionChanges";
import { orderUnknownStateVisibilityFn } from "../../visibilityFunctions/orderStateVisibilityFn";

export const useOrderUnknownBrandNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const articles = useArticles();
  return {
    AddOrEditItemsUnknownState: {
      id: "AddOrEditItemsUnknownState",
      label: strictLocalize({
        defaultMessage: "Add or edit items in order",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      isNodeVisible: orderUnknownStateVisibilityFn,
      instantAnswer: addOrEditItems(strictLocalize),
      articles: [articles.FulfillingOrders.EDIT_RETAILER_ORDER],
    },
    EditOrderInformationUnknownState: {
      id: "EditOrderInformationUnknownState",
      label: strictLocalize({
        defaultMessage: "Edit order information",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      isNodeVisible: orderUnknownStateVisibilityFn,
      instantAnswer: editOrderInformation(strictLocalize),
      articles: [articles.FulfillingOrders.EDIT_RETAILER_ORDER],
    },
    InternationalOrderSettingsUnknownState: {
      id: "InternationalOrderSettingsUnknownState",
      label: strictLocalize({
        defaultMessage: "International order settings",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      isNodeVisible: orderUnknownStateVisibilityFn,
      instantAnswer: internationalOrderSettings(strictLocalize),
      articles: [articles.International.LOCATION_SHOP_LIVE],
    },
    DamageMissingItemReportUnknownState: {
      id: "DamageMissingItemReportUnknownState",
      label: strictLocalize({
        defaultMessage: "Damaged or missing item report",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      instantAnswer: damageMissing(strictLocalize),
      isNodeVisible: orderUnknownStateVisibilityFn,
    },
    RequestCommissionChangesUnknownState: {
      id: "RequestCommissionChangesUnknownState",
      label: strictLocalize({
        defaultMessage: "Request commission change",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      isNodeVisible: orderUnknownStateVisibilityFn,
      instantAnswer: RequestCommissionChanges(strictLocalize),
      articles: [articles.FaireDirect.ZERO_COMMISSION],
    },
    CancelOrderUnknownState: {
      id: "CancelOrderUnknownState",
      label: strictLocalize({
        defaultMessage: "Cancel order",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      isNodeVisible: orderUnknownStateVisibilityFn,
      instantAnswer: cancelOrder(strictLocalize),
      articles: [articles.FulfillingOrders.FULFILLING_ORDER],
    },
    CannotFindAnOrderUnknownState: {
      id: "CannotFindAnOrderUnknownState",
      label: strictLocalize({
        defaultMessage: "Unable to find order",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      isNodeVisible: orderUnknownStateVisibilityFn,
      instantAnswer: cannotFindAnOrder(strictLocalize),
      articles: [
        articles.Account.ORDER_ISSUE,
        articles.Account.CANNOT_FIND_ORDER_NUMBER,
      ],
    },
  };
};
